// @ts-strict-ignore
import { useEffect, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { logSentryErrorAndGetEventId } from '@node-space/utils'
import { PathNames } from 'constants/General'
import { AccountType } from 'types/accounts'
import { AccountTypes } from 'types/types'
import { VerificationStatuses } from 'types/verification'
import { useAccountsQuery, useProfileQuery } from './queries'
import { useVerificationRequestStatusQuery } from './queries/useVerificationRequestStatusQuery'

interface ErrorType {
  status: number
}

export const useRequestsOnLogin = () => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const {
    data: profile,
    isFetching: isFetchingProfile,
    isFetched: isFetchedProfile,
    isError: isErrorProfile,
    error: errorProfile,
  } = useProfileQuery()

  const {
    data: accounts,
    isFetching: isFetchingAccounts,
    isFetched: isFetchedAccounts,
    isError: isErrorUserAccount,
    error: errorUserAccounts,
  } = useAccountsQuery(isFetchedProfile)

  const currentAccount: AccountType = accounts?.find(account => account?.isCurrent)

  const {
    data: verificationRequestStatus,
    isFetching: isFetchingVerificationRequestStatus,
    isFetched: isFetchedVerificationRequestStatus,
    isError: isErrorVerificationRequestStatus,
    error: errorVerificationRequestStatus,
  } = useVerificationRequestStatusQuery(isFetchedProfile)

  const isLoading = isFetchingProfile && isFetchingAccounts && isFetchingVerificationRequestStatus
  const isError = isErrorProfile || isErrorUserAccount || isErrorVerificationRequestStatus
  const error = errorProfile || errorUserAccounts || errorVerificationRequestStatus

  useEffect(() => {
    if (!isLoading && isError) {
      const endpoints = {
        profile: 'user/profile',
        userAccounts: 'account/user-accounts',
        verificationStatus: 'business/account/verification-request-status',
      }

      const endpoint = errorProfile
        ? endpoints.profile
        : errorUserAccounts
          ? endpoints.userAccounts
          : endpoints.verificationStatus

      const sentryEventId = logSentryErrorAndGetEventId(
        `Failed API request to '/${endpoint}' with status ${(error as unknown as ErrorType)?.status}`,
        error,
        {
          function: 'useRequestsOnLogin',
        },
        'fatal'
      )

      queryClient.cancelQueries().then(() => {
        navigate(PathNames.ACCOUNT_ERROR, {
          state: sentryEventId,
        })
      })
    }
  }, [isLoading, isError, error])

  const isVerifiedBusinessAccount = useMemo(
    () => verificationRequestStatus === VerificationStatuses.VERIFIED,
    [verificationRequestStatus]
  )

  const isDeveloperAccount = useMemo(
    () => profile?.options?.registrationAccountType === AccountTypes.DEVELOPER,
    [profile?.options?.registrationAccountType]
  )

  const isAuthorizedAccount = useMemo(
    () => isVerifiedBusinessAccount || isDeveloperAccount,
    [isVerifiedBusinessAccount, isDeveloperAccount]
  )

  const isLoadingRequestsOnLogin = useMemo(
    () => isFetchingProfile || isFetchingVerificationRequestStatus || isFetchingAccounts,
    [isFetchingProfile, isFetchingVerificationRequestStatus, isFetchingAccounts]
  )

  const fetchedRequest = {
    profile: isFetchedProfile,
    verification: isFetchedVerificationRequestStatus,
    account: isFetchedAccounts,
  }

  return {
    isLoadingRequestsOnLogin,
    fetchedRequest,
    profile,
    isVerifiedBusinessAccount,
    verificationRequestStatus,
    isDeveloperAccount,
    isAuthorizedAccount,
    accounts,
    currentAccount,
  }
}
